import { IconDefinition } from "@fortawesome/fontawesome-svg-core"
import { ClientTypes } from "@legacy/core/utils/enums"

import { JOB_ORIGIN_TYPE_ABBREVIATIONS, PRICEBOOK_ITEM_TYPE_MAP } from "@constants/mappings"
import { objectMap } from "@constants/objectMap"

export function getSearchResultIcon(indexName: ObjectIndexName): IconDefinition {
    switch (indexName) {
        case "Estimate":
            return objectMap.estimate.icon
        case "Job":
            return objectMap.job.icon
        case "JobSeries":
            return objectMap.job_series.icon
        case "Invoice":
            return objectMap.invoice.icon
        case "CreditNote":
            return objectMap.credit_note.icon
        case "PurchaseOrder":
            return objectMap.purchase_order.icon
        case "Bill":
            return objectMap.bill.icon
        case "Vendor":
            return objectMap.vendor.icon
        case "Client":
            return objectMap.client.icon
        case "PriceBookItem":
            return objectMap.pricebook_item.icon
        case "CatalogItem":
            return objectMap.catalog_item.icon
    }
}

export function getSearchResultPrimaryText(indexName: ObjectIndexName, index: ObjectsIndex): string {
    switch (indexName) {
        case "Estimate":
            return index.custom_id || index.id
        case "Job":
            return index.custom_id || index.id
        case "JobSeries":
            return index.service_name
        case "Invoice":
            return index.custom_id || index.id
        case "CreditNote":
            return index.custom_id || index.id
        case "PurchaseOrder":
            return index.custom_id || index.id
        case "Bill":
            return index.custom_id || index.id
        case "Vendor":
            return index.name
        case "Client":
            return index.name
        case "PriceBookItem":
            return index.description
        case "CatalogItem":
            return index.name
    }
}

function formatOriginText(originTypeLabel: string, originId: string): string {
    const abbr = JOB_ORIGIN_TYPE_ABBREVIATIONS[originTypeLabel]
    // If abbreviation exists (not undefined), use it. If it's empty string, just show ID
    if (abbr !== undefined) {
        return abbr ? `${abbr} ${originId}` : originId
    } else {
        // Fallback for unknown origin types
        return `${originTypeLabel} ${originId}`
    }
}

export function getSearchResultSecondaryText(indexName: ObjectIndexName, index: ObjectsIndex): string {
    switch (indexName) {
        case "Estimate": {
            const estimateIndex = index as EstimateIndex
            let text = estimateIndex.client
            if (estimateIndex.job_walk_origin_type_label && estimateIndex.job_walk_origin_id) {
                text += ` \u2022 ${formatOriginText(estimateIndex.job_walk_origin_type_label, estimateIndex.job_walk_origin_id)}`
            }
            return text
        }
        case "Job": {
            const jobIndex = index as JobIndex
            let text = jobIndex.service_name
            if (jobIndex.origin_type_label && jobIndex.origin_id) {
                text += ` \u2022 ${formatOriginText(jobIndex.origin_type_label, jobIndex.origin_id)}`
            }
            return text
        }
        case "JobSeries":
            return ""
        case "Invoice": {
            const invoiceIndex = index as InvoiceIndex
            let text = invoiceIndex.client
            if (invoiceIndex.job_origin_type_label && invoiceIndex.job_origin_id) {
                text += ` \u2022 ${formatOriginText(invoiceIndex.job_origin_type_label, invoiceIndex.job_origin_id)}`
            }
            return text
        }
        case "CreditNote": {
            const creditNoteIndex = index as CreditNoteIndex
            let text = creditNoteIndex.client
            if (creditNoteIndex.invoice_id) {
                text += ` \u2022 ${creditNoteIndex.invoice_custom_id ?? creditNoteIndex.invoice_id}`
            }
            return text
        }
        case "PurchaseOrder":
            return index.vendor
        case "Bill":
            return `${index.vendor} \u2022 ${index.vendor_invoice_id}`
        case "Vendor":
            return ""
        case "Client":
            return index.client_type === ClientTypes.business ? "Business" : "Individual"
        case "PriceBookItem":
            return PRICEBOOK_ITEM_TYPE_MAP[index.pricebook_item_type]
        case "CatalogItem":
            return ""
    }
}
